import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { assetQueries } from '@/lib/queries/assets';
import { supabase } from '@/lib/supabase';

/** Natural dimensions from a decoded image — cheap, prevents layout shift (CLS). */
function getImageDimensions(url: string): Promise<{ width: number | null; height: number | null }> {
  return new Promise((resolve) => {
    const im = new window.Image();
    im.onload = () => resolve({ width: im.naturalWidth || null, height: im.naturalHeight || null });
    im.onerror = () => resolve({ width: null, height: null });
    im.src = url;
  });
}

/**
 * The Image insert modal (Spec 40 §5). Two tabs: Upload new (prompt for alt +
 * caption, then upload + insert the assets row) and Choose from library (click an
 * existing asset → insert the node, no upload, no metadata prompt — the payoff of
 * reference-by-ID). Both end by handing an assetId back to the composer.
 */
export function ImageModal({
  open,
  onClose,
  userId,
  onInsert,
}: {
  open: boolean;
  onClose: () => void;
  userId: string | undefined;
  onInsert: (assetId: string) => void;
}) {
  const [tab, setTab] = useState<'upload' | 'library'>('upload');
  const [pick, setPick] = useState<{ file: File; url: string } | null>(null);
  const [alt, setAlt] = useState('');
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const library = useQuery({ ...assetQueries.list({ userId, all: false }), enabled: open });
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = library.data ?? [];
    if (!q) return rows;
    return rows.filter((a) =>
      [a.altText, a.caption ?? '', a.credit ?? ''].some((f) => f.toLowerCase().includes(q)),
    );
  }, [library.data, search]);

  if (!open) return null;

  const clearPick = () => {
    if (pick) URL.revokeObjectURL(pick.url);
    setPick(null);
    setAlt('');
    setCaption('');
  };
  const close = () => {
    clearPick();
    setSearch('');
    onClose();
  };
  const insertAndClose = (assetId: string) => {
    onInsert(assetId);
    close();
  };

  const onFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('That file is not an image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB.');
      return;
    }
    setAlt('');
    setCaption('');
    setPick({ file, url: URL.createObjectURL(file) });
  };

  const uploadAndInsert = async () => {
    if (!userId || !pick || !alt.trim()) return;
    setBusy(true);
    try {
      const dims = await getImageDimensions(pick.url);
      const ext = pick.file.name.split('.').pop() ?? 'jpg';
      const path = `${userId}/asset-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('content-images')
        .upload(path, pick.file, { contentType: pick.file.type });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from('content-images').getPublicUrl(path);
      const { data: asset, error: insErr } = await supabase
        .from('assets')
        .insert({
          storage_path: path,
          public_url: pub.publicUrl,
          alt_text: alt.trim(),
          caption: caption.trim() || null,
          width: dims.width,
          height: dims.height,
          uploaded_by: userId,
        })
        .select('id')
        .single();
      if (insErr) throw insErr;
      insertAndClose(asset.id);
      toast.success('Image inserted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Image upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg border border-ink/10 bg-parchment p-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {(['upload', 'library'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  tab === t ? 'bg-ink/10 font-medium text-ink' : 'text-ink/60 hover:text-ink'
                }`}
              >
                {t === 'upload' ? 'Upload new' : 'Choose from library'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={close}
            className="font-sans text-sm text-ink/50 hover:text-oxblood"
          >
            Close
          </button>
        </div>

        {tab === 'upload' ? (
          <div className="mt-5">
            {!pick ? (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex h-40 w-full items-center justify-center rounded-md border border-dashed border-ink/25 font-sans text-sm text-ink/50 hover:border-oxblood hover:text-oxblood"
              >
                Choose an image file
              </button>
            ) : (
              <>
                <img src={pick.url} alt="" className="max-h-56 w-full rounded-md object-contain" />
                <label className="mt-4 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
                  Alt text (required)
                </label>
                <input
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  placeholder="Describe the image for screen readers"
                  className="mt-1 w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
                />
                <label className="mt-3 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
                  Caption (optional)
                </label>
                <input
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="mt-1 w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
                />
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={clearPick}
                    disabled={busy}
                    className="rounded-md border border-ink/20 px-4 py-2 text-sm text-ink hover:border-oxblood disabled:opacity-60"
                  >
                    Choose different
                  </button>
                  <button
                    type="button"
                    onClick={() => void uploadAndInsert()}
                    disabled={busy || !alt.trim()}
                    className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-50"
                  >
                    {busy ? 'Uploading…' : 'Insert'}
                  </button>
                </div>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
                e.target.value = '';
              }}
            />
          </div>
        ) : (
          <div className="mt-5 flex min-h-0 flex-1 flex-col">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search your images by alt, caption, or credit…"
              className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
            />
            <div className="mt-3 grid grid-cols-3 gap-2 overflow-y-auto">
              {filtered.length === 0 && (
                <p className="col-span-3 py-8 text-center font-sans text-sm text-ink/50">
                  {library.isLoading ? 'Loading…' : 'No images yet — upload one.'}
                </p>
              )}
              {filtered.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => insertAndClose(a.id)}
                  title={a.caption ?? a.altText}
                  className="group overflow-hidden rounded-md border border-ink/10 hover:border-oxblood"
                >
                  <img
                    src={a.publicUrl}
                    alt={a.altText}
                    className="aspect-square w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
